import { useQuery } from "@tanstack/react-query";
import { authMe, authLogout, ApiError, type AuthMe } from "./api";

const TOKEN_KEY = "catho.jwt";
const REFRESH_KEY = "catho.refresh";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

/** Token de rafraîchissement (7 j) — sert au refresh silencieux (cf. api.ts). */
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function setRefreshToken(token: string): void {
  try {
    localStorage.setItem(REFRESH_KEY, token);
  } catch {
    /* ignore */
  }
}

/**
 * Purge la session locale : access token ET refresh token. Appelé quand la
 * session est morte (401 non rattrapable par le refresh) ou à la déconnexion.
 */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Returns the current authenticated user (or undefined if none).
 *
 * NOTE — Auth endpoints (/api/auth/me) may not be exposed at the gateway
 * yet during S3. If the call returns 401/404 we simply treat the user as
 * unauthenticated; the parent journey is fully usable in guest mode.
 */
export function useCurrentUser() {
  const token = getToken();
  const { data, isLoading, refetch } = useQuery<AuthMe | null>({
    queryKey: ["auth", "me", token],
    enabled: !!token,
    // Un 401 = session réellement invalide → null (les gardes renvoient au login).
    // Un 429/5xx/réseau = blip transitoire (limiteur edge) → on RETENTE plutôt
    // que d'éjecter une session authentifiée vers /auth/login. Sans ça, un seul
    // hoquet sur /api/auth/me déconnecte l'utilisateur en pleine navigation.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 2,
    queryFn: async () => {
      try {
        return await authMe();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
  });

  const logout = async () => {
    // Vraie déconnexion : on révoque la session côté serveur (POST
    // /api/auth/logout invalide session + refresh token) AVANT de purger le
    // local. Best-effort : un échec réseau/hors-ligne ne doit jamais empêcher
    // la déconnexion locale ni bloquer la redirection.
    try {
      await authLogout();
    } catch {
      /* réseau/échec/permission → on déconnecte quand même localement */
    }
    clearToken();
    window.location.href = "/auth/login";
  };

  return {
    user: data ?? undefined,
    isLoading: !!token && isLoading,
    isAuthenticated: !!data,
    logout,
    refetch,
  };
}
